import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getApiErrorCode,
  getApiErrorDetails,
  getApiErrorMessage,
  stockAPI,
  transfersAPI,
  type StockShortageDto,
  type TransferDetailDto,
  type TransferStoresDto,
} from '@matix/api-client';
import type { CatalogItem } from '@matix/types';
import { sellingUnitOf } from '../../lib/priceTier';
import { useLang } from '../../lib/language';
import { nameFor } from '../../lib/localizedName';
import { toast } from '../../store/toast';
import { useStockLevels } from '../../store/stockLevels';
import { useAllItems } from '../catalog/hooks';
import { SearchIcon, TrashIcon, XIcon } from '../../components/icons';
import { storeName, TILL_CANCELS_AND_TRANSFERS_NOW } from './shared';

interface DraftLine {
  key: string;
  item: CatalogItem;
  unitId: number | null;
  qty: number;
  notes: string;
}

/**
 * Raise a transfer request.
 *
 * The item box is the same catalogue the till sells from, matched the same
 * way (name, Arabic name, code, any unit barcode) — so a scanner works here
 * exactly as it does on the sale screen, and a scanned code adds the line
 * without a click. Every line shows what the SOURCE store holds right now;
 * when the company blocks negative stock a short line disables saving, since
 * the server would refuse it anyway.
 *
 * Two ways out: "Save request" (the destination receives it later) and, when
 * this branch IS the destination, "Transfer now" — request and goods
 * document in one go.
 */
export function NewTransferPanel(props: {
  stores: TransferStoresDto;
  onClose: () => void;
  onCreated: (detail: TransferDetailDto) => void;
}): JSX.Element {
  const { t, lang } = useLang();
  const openShortageDialog = useStockLevels((s) => s.openDialog);
  const allItems = useAllItems();
  const { stores, my_store_id: myStoreId, block_negative_stock: blockNegative } = props.stores;

  const [toStoreId, setToStoreId] = useState<number>(() => myStoreId ?? stores[0]?.id ?? 0);
  const [fromStoreId, setFromStoreId] = useState<number>(
    () => stores.find((s) => s.id !== (myStoreId ?? stores[0]?.id))?.id ?? 0,
  );
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [term, setTerm] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState<'request' | 'now' | null>(null);
  // Keyed `"<item id>:<unit id>"`, plus a `"<item id>:"` row per item as the
  // fallback for an API that sent no per-unit levels.
  const [levels, setLevels] = useState<Record<string, { onHand: number; unit: string | null; unitAr: string | null }>>({});
  const [shortItems, setShortItems] = useState<Set<number>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const id = window.setTimeout(() => inputRef.current?.focus(), 60);
    return () => window.clearTimeout(id);
  }, []);

  // Live source balance for the draft, debounced as the cashier types.
  useEffect(() => {
    if (!fromStoreId || lines.length === 0) {
      setLevels({});
      setShortItems(new Set());
      return;
    }
    let cancelled = false;
    const id = window.setTimeout(() => {
      stockAPI
        .check({
          store_id: fromStoreId,
          lines: lines.map((l) => ({ item_id: l.item.id, unit_id: l.unitId, qty: l.qty })),
        })
        .then((res) => {
          if (cancelled) return;
          // Keyed by `item:unit`, because the figure sits in the same row as a
          // Quantity input in the LINE's unit. `res.levels` is per item in the
          // item's inventory unit, so a line transferring cartons used to show
          // the piece count beside it.
          const next: typeof levels = {};
          for (const lv of res.levels) {
            next[`${lv.item_id}:`] = { onHand: lv.on_hand, unit: lv.unit_name, unitAr: lv.unit_name_ar };
          }
          for (const lv of res.unit_levels ?? []) {
            next[`${lv.item_id}:${lv.unit_id}`] = {
              onHand: lv.on_hand,
              unit: lv.unit_name,
              unitAr: lv.unit_name_ar,
            };
          }
          setLevels(next);
          setShortItems(new Set(res.shortages.map((s) => s.item_id)));
        })
        .catch(() => undefined);
    }, 300);
    return () => { cancelled = true; window.clearTimeout(id); };
  }, [fromStoreId, lines]);

  const results = useMemo(() => {
    const q = term.trim();
    if (!q) return [];
    return allItems.filter((it) => matches(it, q)).slice(0, 12);
  }, [allItems, term]);

  function addItem(item: CatalogItem): void {
    setLines((prev) => {
      const existing = prev.find((l) => l.item.id === item.id);
      if (existing) {
        return prev.map((l) => (l.item.id === item.id ? { ...l, qty: l.qty + 1 } : l));
      }
      // Same resolution as the till's tiles: a POS unit beats Selling_UnitId.
      const unit = sellingUnitOf(item) ?? item.units[0] ?? null;
      return [
        ...prev,
        { key: `${item.id}-${Date.now()}`, item, unitId: unit?.unit_id ?? null, qty: 1, notes: '' },
      ];
    });
    setTerm('');
    inputRef.current?.focus();
  }

  function onSearchKey(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const q = term.trim();
    if (!q) return;
    const exact = allItems.find((it) => exactScan(it, q));
    const pick = exact ?? results[0];
    if (pick) addItem(pick);
    else toast(t({ en: 'No item matches that code', ar: 'لا يوجد صنف مطابق لهذا الكود' }), 'warning');
  }

  function patch(key: string, p: Partial<DraftLine>): void {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...p } : l)));
  }

  const sameStore = fromStoreId === toStoreId;
  const receivingHere = myStoreId != null && toStoreId === myStoreId;
  const showTransferNow = TILL_CANCELS_AND_TRANSFERS_NOW && receivingHere;
  const anyShort = lines.some((l) => shortItems.has(l.item.id));
  const invalidQty = lines.some((l) => !(l.qty > 0));
  const blocked = blockNegative && anyShort;
  const canSave = lines.length > 0 && !sameStore && !invalidQty && !blocked && saving === null;

  async function submit(receiveNow: boolean): Promise<void> {
    if (!canSave) return;
    setSaving(receiveNow ? 'now' : 'request');
    try {
      const detail = await transfersAPI.create({
        from_store_id: fromStoreId,
        to_store_id: toStoreId,
        reference_no: reference.trim() || null,
        notes: notes.trim() || null,
        receive_now: receiveNow,
        lines: lines.map((l) => ({
          item_id: l.item.id,
          unit_id: l.unitId,
          qty: l.qty,
          notes: l.notes.trim() || null,
        })),
      });
      toast(
        receiveNow
          ? t({ en: `Transfer #${detail.entry_no} done · goods document #${detail.received_entry_no ?? '—'}`, ar: `تم التحويل #${detail.entry_no} · سند التحويل #${detail.received_entry_no ?? '—'}` })
          : t({ en: `Transfer request #${detail.entry_no} saved`, ar: `تم حفظ طلب التحويل #${detail.entry_no}` }),
        'success',
      );
      props.onCreated(detail);
    } catch (err) {
      if (getApiErrorCode(err) === 'INSUFFICIENT_STOCK') {
        const details = getApiErrorDetails<{ shortages?: StockShortageDto[] }>(err);
        openShortageDialog(details?.shortages ?? [], true);
      } else {
        toast(getApiErrorMessage(err), 'error');
      }
    } finally {
      setSaving(null);
    }
  }

  const fromStore = stores.find((s) => s.id === fromStoreId);
  const toStore = stores.find((s) => s.id === toStoreId);

  return (
    <div className="modal-overlay show" onClick={props.onClose}>
      <div className="modal tr-new-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-img" style={{ background: 'var(--primary-light)', color: 'var(--primary)' }}>⇄</div>
          <div className="modal-header-title">
            <div className="modal-header-name">{t({ en: 'New store transfer', ar: 'تحويل مخزني جديد' })}</div>
            <div className="modal-header-name-ar">
              {t({ en: 'Pick the two warehouses, then scan or search the items.', ar: 'اختر المخزنين، ثم امسح الأصناف أو ابحث عنها.' })}
            </div>
          </div>
          <button type="button" className="modal-close" onClick={props.onClose} aria-label={t({ en: 'Close', ar: 'إغلاق' })}>
            <XIcon size={18} />
          </button>
        </div>

        <div className="modal-body tr-new-body">
          <div className="tr-stores-row">
            <label className="tr-field">
              <span>{t({ en: 'From warehouse', ar: 'من مخزن' })}</span>
              <select className="cc-select" value={fromStoreId} onChange={(e) => setFromStoreId(Number(e.target.value))}>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {storeName(s, lang)}{s.is_mine ? ` — ${t({ en: 'your store', ar: 'مخزنك' })}` : ''}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="tr-swap"
              title={t({ en: 'Swap direction', ar: 'عكس الاتجاه' })}
              onClick={() => { setFromStoreId(toStoreId); setToStoreId(fromStoreId); }}
            >
              ⇄
            </button>
            <label className="tr-field">
              <span>{t({ en: 'To warehouse', ar: 'إلى مخزن' })}</span>
              <select className="cc-select" value={toStoreId} onChange={(e) => setToStoreId(Number(e.target.value))}>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {storeName(s, lang)}{s.is_mine ? ` — ${t({ en: 'your store', ar: 'مخزنك' })}` : ''}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className={`tr-hint${sameStore ? ' is-error' : ''}`}>
            {sameStore
              ? t({ en: 'Choose two different warehouses.', ar: 'اختر مخزنين مختلفين.' })
              : receivingHere
                ? showTransferNow
                  ? t({
                      en: `The goods come to your store. Save it as a request for later, or transfer now if they are already here.`,
                      ar: 'البضاعة قادمة إلى مخزنك. احفظه كطلب لاستلامه لاحقاً، أو حوّل الآن إذا كانت البضاعة موجودة بالفعل.',
                    })
                  : t({
                      en: 'The goods come to your store. Save it as a request; it is received in the ERP.',
                      ar: 'البضاعة قادمة إلى مخزنك. احفظه كطلب، ويتم استلامه من النظام (ERP).',
                    })
                : fromStoreId === myStoreId
                  ? t({
                      en: `Goods leave your store. ${toStore ? storeName(toStore, lang) : ''} confirms when they arrive; stock moves at that moment.`,
                      ar: `البضاعة تخرج من مخزنك. يؤكد ${toStore ? storeName(toStore, lang) : ''} عند وصولها، وينتقل الرصيد في تلك اللحظة.`,
                    })
                  : t({
                      en: 'Neither warehouse is yours: the destination branch receives it.',
                      ar: 'لا يخصك أي من المخزنين: يستلمه فرع الوجهة.',
                    })}
          </div>

          <div className="stock-balance-search tr-item-search">
            <SearchIcon size={16} />
            <input
              ref={inputRef}
              type="text"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={onSearchKey}
              placeholder={t({ en: 'Scan a barcode or search by name / code…', ar: 'امسح الباركود أو ابحث بالاسم / الكود…' })}
            />
            {term && (
              <button type="button" className="stock-balance-clear" onClick={() => { setTerm(''); inputRef.current?.focus(); }} aria-label={t({ en: 'Clear', ar: 'مسح' })}>
                <XIcon size={14} />
              </button>
            )}
          </div>
          {results.length > 0 && (
            <ul className="stock-balance-results tr-results">
              {results.map((it) => (
                <li key={it.id}>
                  <button type="button" onClick={() => addItem(it)}>
                    <span className="sb-result-code">{it.code}</span>
                    <span className="sb-result-name">{nameFor(lang, it, `#${it.id}`)}</span>
                    {it.item_type === 2 && <span className="sb-result-tag">{t({ en: 'service', ar: 'خدمة' })}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="stock-balance-table-wrap tr-lines-wrap">
            <table className="stock-balance-table tr-lines">
              <thead>
                <tr>
                  <th>{t({ en: 'Item', ar: 'الصنف' })}</th>
                  <th>{t({ en: 'Unit', ar: 'الوحدة' })}</th>
                  <th className="sb-qty">{t({ en: 'Quantity', ar: 'الكمية' })}</th>
                  <th className="sb-qty">{fromStore ? t({ en: `At ${storeName(fromStore, lang)}`, ar: `في ${storeName(fromStore, lang)}` }) : t({ en: 'At source', ar: 'بالمصدر' })}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 && (
                  <tr>
                    <td colSpan={5} className="sb-empty">
                      {t({ en: 'No items yet — scan or search above.', ar: 'لا توجد أصناف بعد — امسح أو ابحث أعلاه.' })}
                    </td>
                  </tr>
                )}
                {lines.map((l) => {
                  const lv = levels[`${l.item.id}:${l.unitId ?? ''}`] ?? levels[`${l.item.id}:`];
                  const short = shortItems.has(l.item.id);
                  const unitLabel = lv ? ((lang === 'ar' ? lv.unitAr : lv.unit) || lv.unit || '') : '';
                  return (
                    <tr key={l.key} className={short ? 'is-short' : undefined}>
                      <td>
                        <div>{nameFor(lang, l.item, `#${l.item.id}`)}</div>
                        <div className="tr-line-code">{l.item.code}</div>
                      </td>
                      <td>
                        {l.item.units.length > 1 ? (
                          <select
                            className="cc-select tr-unit-select"
                            value={l.unitId ?? ''}
                            onChange={(e) => patch(l.key, { unitId: e.target.value ? Number(e.target.value) : null })}
                          >
                            {l.item.units.map((u) => (
                              <option key={u.unit_row_id} value={u.unit_id}>
                                {(lang === 'ar' ? u.unit_name_ar : u.unit_name) || u.unit_name || u.unit_id}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span>{(() => { const u = l.item.units[0]; return u ? ((lang === 'ar' ? u.unit_name_ar : u.unit_name) || u.unit_name || '—') : '—'; })()}</span>
                        )}
                      </td>
                      <td className="sb-qty">
                        <input
                          type="number"
                          className="tr-qty"
                          min={l.item.allow_decimals ? 0.001 : 1}
                          step={l.item.allow_decimals ? 0.001 : 1}
                          value={l.qty}
                          onChange={(e) => patch(l.key, { qty: Number(e.target.value) })}
                          onFocus={(e) => e.currentTarget.select()}
                        />
                      </td>
                      <td className={`sb-qty${short ? ' is-short' : ''}`}>
                        {lv ? `${fmtQty(lv.onHand)} ${unitLabel}` : '…'}
                      </td>
                      <td className="tr-line-remove">
                        <button type="button" className="icon-btn" onClick={() => setLines((prev) => prev.filter((x) => x.key !== l.key))} title={t({ en: 'Remove', ar: 'حذف' })}>
                          <TrashIcon size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {anyShort && (
            <div className={`cc-warn tr-warn${blockNegative ? ' is-error' : ''}`}>
              {blockNegative
                ? t({
                    en: 'Some lines exceed what the source warehouse holds. This company does not allow negative stock, so reduce those quantities before saving.',
                    ar: 'بعض الأصناف تتجاوز رصيد المخزن المصدر. لا تسمح هذه الشركة بالرصيد السالب، لذا قلّل تلك الكميات قبل الحفظ.',
                  })
                : t({
                    en: 'Some lines exceed what the source warehouse holds; its balance will go negative when the goods are received.',
                    ar: 'بعض الأصناف تتجاوز رصيد المخزن المصدر؛ سيصبح رصيده بالسالب عند استلام البضاعة.',
                  })}
            </div>
          )}

          <div className="tr-notes-row">
            <label className="tr-field">
              <span>{t({ en: 'Reference', ar: 'المرجع' })}</span>
              <input className="cc-input" value={reference} onChange={(e) => setReference(e.target.value)} maxLength={200} placeholder={t({ en: 'Optional', ar: 'اختياري' })} />
            </label>
            <label className="tr-field tr-field--grow">
              <span>{t({ en: 'Notes', ar: 'ملاحظات' })}</span>
              <input className="cc-input" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={1000} placeholder={t({ en: 'Optional', ar: 'اختياري' })} />
            </label>
          </div>
        </div>

        <div className="modal-footer tr-detail-footer">
          <div className="tr-footer-summary">
            {lines.length > 0 && t({
              en: `${lines.length} item(s) · ${fmtQty(lines.reduce((s, l) => s + (l.qty > 0 ? l.qty : 0), 0))} units`,
              ar: `${lines.length} صنف · ${fmtQty(lines.reduce((s, l) => s + (l.qty > 0 ? l.qty : 0), 0))} وحدة`,
            })}
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={props.onClose} disabled={saving !== null}>
              {t({ en: 'Cancel', ar: 'إلغاء' })}
            </button>
            <button type="button" className={showTransferNow ? 'btn-secondary' : 'btn-primary-modal'} onClick={() => void submit(false)} disabled={!canSave}>
              {saving === 'request' ? t({ en: 'Saving…', ar: 'جارٍ الحفظ…' }) : t({ en: 'Save request', ar: 'حفظ الطلب' })}
            </button>
            {showTransferNow && (
              <button type="button" className="btn-primary-modal" onClick={() => void submit(true)} disabled={!canSave}>
                {saving === 'now' ? t({ en: 'Transferring…', ar: 'جارٍ التحويل…' }) : t({ en: 'Transfer now', ar: 'تحويل الآن' })}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Same matching the sale screen uses: name, Arabic name, code, any unit barcode. */
function matches(item: CatalogItem, rawQuery: string): boolean {
  const ar = rawQuery.trim();
  const q = ar.toLowerCase();
  if (!q) return false;
  if (item.name?.toLowerCase().includes(q)) return true;
  if (item.name_ar?.includes(ar)) return true;
  if (item.code.toLowerCase().includes(q)) return true;
  if (item.barcodes?.some((b) => b.toLowerCase().includes(q))) return true;
  return item.units.some((u) => u.barcode?.toLowerCase().includes(q));
}

function exactScan(item: CatalogItem, rawQuery: string): boolean {
  const q = rawQuery.trim().toLowerCase();
  if (item.code.toLowerCase() === q) return true;
  if (item.barcodes?.some((b) => b.toLowerCase() === q)) return true;
  return item.units.some((u) => u.barcode?.toLowerCase() === q);
}

function fmtQty(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 3 });
}
